import { jsx as _jsx } from "react/jsx-runtime";
import { getSourceByIdQueryKey } from '#pplx/frontend/apps/ask/queries/query-keys/sourcesKeys.js';
import { toTitleCase } from '#pplx/frontend/apps/ask/shared-helpers/stringUtils.js';
import { fetchSource } from '#pplx/frontend/apps/ask/sources/hooks/fetchSource.js';
import { getAvatarSizeForIconSize } from '#pplx/frontend/apps/ask/sources/utilities/getAvatarSizeForIconSize.js';
import Avatar from '#pplx/frontend/components/user/Avatar.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
export const useApiSource = ({ sourceId, reason, enabled = true, bypassCache = false, resolveLink = false, }) => {
    const queryClient = useQueryClient();
    const refreshOnAccess = bypassCache || resolveLink;
    const query = useQuery({
        queryKey: resolveLink
            ? [...getSourceByIdQueryKey(sourceId), 'resolve-link']
            : getSourceByIdQueryKey(sourceId),
        queryFn: () => fetchSource({ queryClient, sourceId, reason, bypassCache, resolveLink }),
        enabled,
        retry: false,
        staleTime: refreshOnAccess ? 0 : undefined,
        gcTime: refreshOnAccess ? 0 : undefined,
        refetchOnMount: refreshOnAccess ? 'always' : undefined,
        refetchOnWindowFocus: refreshOnAccess ? 'always' : undefined,
        meta: refreshOnAccess ? { skipPersistence: true } : undefined,
    });
    const source = useMemo(() => {
        return query.data?.source ?? null;
    }, [query.data]);
    const label = useMemo(() => {
        return source?.display_name ?? toTitleCase(sourceId);
    }, [source, sourceId]);
    const description = useMemo(() => {
        return source?.description;
    }, [source]);
    const getIcon = useCallback((size = 'default') => {
        const iconUrl = source?.icon_url;
        if (!iconUrl) {
            return undefined;
        }
        return (_jsx(Avatar, { user: iconUrl, size: getAvatarSizeForIconSize(size), rounded: false, showBackground: false, altText: label }));
    }, [source, label]);
    const isConnected = useMemo(() => {
        return query.data?.isConnected ?? false;
    }, [query.data]);
    return useMemo(() => ({
        source,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        isError: query.isError,
        error: query.error ? query.error : undefined,
        label,
        description,
        getIcon,
        isConnected,
        refetch: query.refetch,
    }), [
        source,
        query.isLoading,
        query.isFetching,
        query.isError,
        query.error,
        label,
        description,
        getIcon,
        isConnected,
        query.refetch,
    ]);
};
//# sourceMappingURL=useApiSource.js.map